<template>
  <div v-if="localModel">
    <div class="border-b border-border py-4 text-base">
      <div class="mb-4">{{ t['w.global.selected_option']() }}</div>
      <div class="font-semibold">{{ localModel.name }}</div>
      <div v-if="localModel.isOptionAvailable === 0" class="mt-1 text-sm text-text-attention">{{ t['w.global.product_option.not_available']() }}</div>
      <div v-else-if="localModel.isOptionAvailable === null" class="mt-1 text-sm text-text-attention">{{ t['w.global.product_option.not_available_variation']() }}</div>
    </div>
  </div>
  <div v-if="isSearchVisible" class="relative mt-7">
    <Icon name="search" class="absolute left-5 top-3 size-6" />
    <input
      v-model="searchQuery"
      type="text"
      class="!box-border w-full rounded-full border-border-medium py-3.5 pl-12 pr-4 text-text-medium transition-colors hover:border-border-medium-hover focus:outline-none"
      :placeholder="t['w.global.product_option.search']()"
    />
  </div>
  <div class="mt-4">
    <div v-for="option of filteredOptions" :key="option.id">
      <input :id="option.id" v-model="localModel" type="radio" :name="`option-${option.id}`" :value="option" class="hidden" @input="onInput(option)" />
      <label
        :for="option.id"
        class="flex w-full cursor-pointer flex-col px-1 py-3 hover:text-text-medium focus:text-text-strong active:text-text-strong"
        :class="{ 'text-text-strong': isOptionSelected(option.id) }"
      >
        <div class="flex text-base font-semibold">
          {{ option.name }}
          <InfoTooltip v-if="option.description" class="ml-1">{{ option.description }}</InfoTooltip>
        </div>
        <template v-if="(option && !option.isOptionAvailable) || option.isOptionAvailable === null">
          <div v-if="option.isOptionAvailable === 0 || (option.isOptionAvailable === null && optionGroupCount === 1)" class="mt-1 text-sm text-text-attention">
            {{ t['w.global.product_option.not_available']() }}
          </div>
          <div v-if="option.isOptionAvailable === null && optionGroupCount > 1" class="mt-1 text-sm text-text-attention">
            {{ t['w.global.product_option.not_available_variation']() }}
          </div>
        </template>
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from '~/components/info-tooltip/InfoTooltip.vue'
import * as t from '~/messages'

const props = defineProps({
  maxCountForRadioButtons: { type: Number, default: 6 },
  options: { type: Array, default: null },
  model: { type: Object, default: null },
  optionGroupId: { type: Number, default: null },
  optionGroupTitle: { type: String, default: null },
  optionGroupDescription: { type: String, default: null },
  isOptionAvailable: { type: Function, default: null },
  selectedOptionIds: { type: Set, default: null },
  optionGroupCount: { type: Number, default: null },
})

const localModel = ref(props.model)
const options = ref(props.options)
const searchQuery = ref('')
const isSearchVisible = options.value.length >= 6

const filteredOptions = computed(() => {
  if (!searchQuery.value && !isSearchVisible) {
    return options.value
  }
  const query = searchQuery.value.toLowerCase()

  return options.value.filter((option) => option.name.toLowerCase().includes(query))
})

const emit = defineEmits<{
  'selected-option': [option: unknown]
  'local-model': [localModel: unknown]
}>()

const onInput = (option) => {
  emit('selected-option', option)
  emit('local-model', localModel.value)
}

watch(
  () => props.model,
  (val) => {
    localModel.value = val
  },
  {
    immediate: true,
  }
)

watch(
  () => localModel.value,
  (val) => {
    if (val) {
      onInput(val)
    }
  },
  {
    immediate: true,
  }
)

watch(
  () => props.selectedOptionIds,
  () => {
    props.options.forEach((o) => {
      o.isOptionAvailable = props.isOptionAvailable(o.id, props.optionGroupId)
    })
  },
  {
    immediate: true,
  }
)

const isOptionSelected = (optionId) => {
  return localModel.value && localModel.value.id === optionId
}
</script>
